<template>
    <div class="releases-header">
        <Tag
            v-if="unreleasedChanges"
            v-tooltip.top="'View unreleased changes'"
            class="unreleased-tag"
            severity="secondary"
            icon="pi pi-circle-fill"
            :value="`${unreleasedChanges.totalFiles} file${unreleasedChanges.totalFiles !== 1 ? 's' : ''} changed since ${unreleasedChanges.latestRelease}`"
            @click="
                openReleaseDiffDialog(
                    unreleasedChanges.latestRelease,
                    undefined
                )
            "
        />
        <Button
            icon="pi pi-plus"
            label="Create Release"
            @click="openCreateReleaseDialog"
        />
    </div>

    <div v-if="isLoading && !releases?.length" class="loading-state">
        <ProgressSpinner />
        <p>Loading releases...</p>
    </div>

    <div v-else-if="!releases?.length" class="empty-state">
        <i class="pi pi-tag empty-icon"></i>
        <div>
            <h3>No releases</h3>
            <p>
                <i>Releases let you tag and track snapshots of your stek.</i>
            </p>
            <Button
                icon="pi pi-plus"
                label="Create Release"
                severity="primary"
                @click="openCreateReleaseDialog"
            />
        </div>
    </div>

    <DataTable
        v-else
        ref="releasesTable"
        :value="releases"
        :loading="isLoading"
        dataKey="name"
        rowHover
    >
        <Column field="name" header="Release" class="name-col">
            <template #body="{ data }">
                <div class="release-name">
                    <i class="pi pi-tag"></i>
                    {{ data.name }}
                </div>
            </template>
        </Column>

        <Column field="description" header="Description">
            <template #body="{ data }">
                <template v-if="data.description">
                    {{ data.description }}
                </template>
                <span v-else class="no-description">No description</span>
            </template>
        </Column>

        <Column field="created_at" header="Created">
            <template #body="{ data }">
                <span v-tooltip.top="formatDateTime(data.created_at)">
                    {{ formatRelativeTime(data.created_at) }}
                </span>
            </template>
        </Column>

        <Column field="created_by" header="Created By">
            <template #body="{ data }">
                {{ data.created_by }}
            </template>
        </Column>

        <Column header="Actions" class="actions-col">
            <template #body="{ data, index }">
                <Button
                    v-tooltip.top="'View changes'"
                    icon="pi pi-file-edit"
                    severity="secondary"
                    text
                    rounded
                    @click="viewDiff(data.name, index)"
                />
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import ProgressSpinner from "primevue/progressspinner";
import { onMounted, ref } from "vue";

import { useReleaseManager, useReleaseStore } from "@/stores/release-store";

const releasesTable = ref();
const { releases, isLoading, unreleasedChanges } = useReleaseManager();
const releaseStore = useReleaseStore();

// Must be called during setup() so useDialog() has access to Vue's inject context
releaseStore.initDialog();

const { openCreateReleaseDialog, openReleaseDiffDialog, listReleases } =
    releaseStore;

onMounted(async () => {
    await listReleases();
});

const viewDiff = (releaseName: string, index: number) => {
    // Get the previous release if available (releases are ordered newest first)
    const previousRelease =
        index < (releases.value?.length ?? 0) - 1
            ? releases.value?.[index + 1]?.name
            : undefined;

    openReleaseDiffDialog(releaseName, previousRelease);
};

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const formatRelativeTime = (dateString: string) => {
    const diffMs = new Date(dateString).getTime() - Date.now();
    const diffSecs = Math.round(diffMs / 1000);
    const diffMins = Math.round(diffSecs / 60);
    const diffHours = Math.round(diffMins / 60);
    const diffDays = Math.round(diffHours / 24);
    const diffWeeks = Math.round(diffDays / 7);
    const diffMonths = Math.round(diffDays / 30);
    const diffYears = Math.round(diffDays / 365);

    if (Math.abs(diffSecs) < 60) return rtf.format(diffSecs, "second");
    if (Math.abs(diffMins) < 60) return rtf.format(diffMins, "minute");
    if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
    if (Math.abs(diffDays) < 7) return rtf.format(diffDays, "day");
    if (Math.abs(diffWeeks) < 5) return rtf.format(diffWeeks, "week");
    if (Math.abs(diffMonths) < 12) return rtf.format(diffMonths, "month");
    return rtf.format(diffYears, "year");
};

const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
};
</script>

<style scoped lang="scss">
.releases-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.unreleased-tag {
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.8;
    }

    :deep(.p-tag-icon) {
        font-size: 0.5rem;
        color: var(--p-red-500) !important;
        display: flex;
        align-items: center;
    }
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    gap: 1rem;
}

.empty-state {
    display: flex;
    flex-direction: column;
    gap: 1em;
    align-items: center;
    text-align: center;
    padding: 3rem;

    > * {
        margin: 0;
    }

    .empty-icon {
        font-size: 3rem;
        color: #6c757d;
    }
}

.release-name {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;

    i {
        color: #6c757d;
    }
}

.no-description {
    color: #9e9e9e;
    font-style: italic;
}

:deep(.p-datatable-thead) > tr > th,
:deep(.p-datatable-tbody) > tr > td {
    &.actions-col {
        width: 100px;
        text-align: right;
    }

    &.name-col {
        width: 200px;
    }
}

:deep(.p-datatable-tbody) > tr > td.actions-col {
    padding-top: 0;
    padding-bottom: 0;
}
</style>
